import React, { useState, useEffect } from "react";
import { DataTableHeader, DataTableBody, DataTableFooter, DataTableSearch } from "./components";
import { Table } from "react-bootstrap";

/*************  ✨ Windsurf Command ⭐  *************/
/**
 * DataTable
 *
 * A React component for displaying tabular data with search, sort, and pagination.
 *
 * @param {Object} props
 * @param {Object[]} props.columns - Column definitions. Each column should have an `id` and `header` property.
 * @param {Object[]} [props.data=[]] - The data to display in the table.
 * @param {boolean} [props.serverSide=false] - Whether the data is stored on the server and should be fetched using the `onQueryChange` callback.
 * @param {number} [props.totalRows] - The total number of rows in the dataset. Required if `serverSide` is `true`.
 * @param {number} [props.defaultPageSize=10] - The initial page size for pagination.
 * @param {Function} [props.onQueryChange] - A callback function to call when the query changes. Will be passed the current query state.
 *
 * @example
 * import React from "react";
 * import DataTable from "./DataTable";
 *
 * const columns = [
 *   { id: "name", header: "Name" },
 *   { id: "age", header: "Age" },
 * ];
 *
 * const data = [
 *   { name: "John", age: 25 },
 *   { name: "Jane", age: 30 },
 * ];
 *
 * const App = () => {
 *   return (
 *     <div>
 *       <DataTable columns={columns} data={data} />
 *     </div>
 *   );
 * };
 */
/*******  31395752-baa1-4027-8cd6-8a44550fe340 *******/
const DataTable = ({
  columns,
  data = [],
  serverSide = false,
  totalRows,
  defaultPageSize = 10,
  onQueryChange,
  filterElement,
  belowSearchElement = null,
  /** When true, table search drives parent (e.g. API) instead of filtering rows locally */
  disableLocalSearch = false,
  onSearchChange,
  /** Optional controlled query state (page, pageSize, search, sort) */
  query: controlledQuery,
  onControlledQueryChange,
}) => {
  const [internalQuery, setInternalQuery] = useState({
    page: 1,
    pageSize: defaultPageSize,
    search: "",
    sort: null,
  });
  const isControlled = controlledQuery !== undefined && typeof onControlledQueryChange === "function";
  const query = isControlled ? controlledQuery : internalQuery;
  const setQuery = isControlled ? onControlledQueryChange : setInternalQuery;
  const [processedData, setProcessedData] = useState(data);
  const [filteredDataLength, setFilteredDataLength] = useState(data.length);

  useEffect(() => {
    if (serverSide && onQueryChange) {
      onQueryChange(query);
    } else {
      let filtered = [...data];

      // Search (skipped when parent handles filtering, e.g. quarterly API search)
      if (query.search && !disableLocalSearch) {
        const term = query.search.toLowerCase();
        filtered = filtered.filter((row) =>
          columns.some((col) => {
            if (!col.searchable) return false;
            const acc = col.searchAccessor ?? col.accessor;
            return String(resolveValue(row, acc))
              .toLowerCase()
              .includes(term);
          })
        );
      }

      // Sort
      if (query.sort) {
        const { columnId, direction } = query.sort;
        filtered.sort((a, b) => {
          const valA = resolveValue(a, columns.find((c) => c.id === columnId)?.accessor);
          const valB = resolveValue(b, columns.find((c) => c.id === columnId)?.accessor);
          if (valA < valB) return direction === "asc" ? -1 : 1;
          if (valA > valB) return direction === "asc" ? 1 : -1;
          return 0;
        });
      }

      // Store filtered data length for pagination
      setFilteredDataLength(filtered.length);

      // Paging
      const start = (query.page - 1) * query.pageSize;
      setProcessedData(filtered.slice(start, start + query.pageSize));
    }
  }, [query, data, columns, serverSide, onQueryChange, disableLocalSearch]);

  useEffect(() => {
    if (typeof onSearchChange === "function") {
      onSearchChange(query.search ?? "");
    }
  }, [query.search, onSearchChange]);

  const total = serverSide ? totalRows : filteredDataLength;

  return (
    <div className="datatable">
      {/* Search box */}
      <DataTableSearch query={query} setQuery={setQuery} filterElement={filterElement} />
      {belowSearchElement}

      {/* Table */}
      <Table responsive>
        <DataTableHeader columns={columns} query={query} setQuery={setQuery} />
        <DataTableBody columns={columns} rows={processedData} query={query} />
      </Table>

      {/* Pagination */}
      <DataTableFooter total={total} query={query} setQuery={setQuery} />
    </div>
  );
}

export default React.memo(DataTable, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.columns === nextProps.columns &&
    prevProps.serverSide === nextProps.serverSide &&
    prevProps.disableLocalSearch === nextProps.disableLocalSearch &&
    prevProps.filterElement === nextProps.filterElement &&
    prevProps.belowSearchElement === nextProps.belowSearchElement &&
    prevProps.query === nextProps.query
  );
});

function resolveValue(row, accessor) {
  if (!accessor) return null;
  return typeof accessor === "function"
    ? accessor(row)
    : accessor.split(".").reduce((acc, key) => acc?.[key], row);
}
